// The arena chart: candles from our own battle price (the server's 15 second candles, grouped to 1 or 3 minutes),
// kept live from the same price stream the arena uses. TradingView's Lightweight Charts draws it; it loads only
// when a chart is first opened, so the lobby stays light.
import { useEffect, useRef, useState } from "react";
import type { CandlestickData, IChartApi, ISeriesApi, LineData, UTCTimestamp } from "lightweight-charts";
import { api } from "./api.js";
import { useGame, type Tick } from "./game.js";
import { go } from "./router.js";

export type Timeframe = 15 | 60 | 180 | "line";
export const TIMEFRAMES: { id: Timeframe; label: string }[] = [
  { id: 15, label: "15s" },
  { id: 60, label: "1m" },
  { id: 180, label: "3m" },
  { id: "line", label: "Line" },
];

/** The time frame stays the same between the chart and full screen, and for the rest of the visit. */
export function savedTimeframe(): Timeframe {
  const v = sessionStorage.getItem("dd:chartTf");
  return v === "line" ? "line" : v === "15" || v === "180" ? (Number(v) as 15 | 180) : 60;
}
export function saveTimeframe(tf: Timeframe): void {
  sessionStorage.setItem("dd:chartTf", String(tf));
}

/** Opens an open arena's chart, and makes it the lobby's pick so LONG / SHORT battle in it. A locked arena's chart stays locked. */
export function openChart(arena: string, open: boolean, select: (p: { arena: string }) => void): void {
  if (!open) return;
  sessionStorage.setItem("dd:chartArena", arena);
  select({ arena });
  go("App76");
}
/** The arena the chart screens show. */
export function chartArena(fallback: string): string {
  return sessionStorage.getItem("dd:chartArena") ?? fallback;
}

type Row = [number, number, number, number, number];
export interface Ohlc {
  o: number;
  h: number;
  l: number;
  c: number;
  t: number;
}

const clock = (sec: number, seconds: boolean) =>
  new Date(sec * 1000).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", ...(seconds && { second: "2-digit" }) });

export interface ChartHandle {
  /** Back to the live candle after scrolling away. */
  now(): void;
}

export function CandleChart({ arena, tf, interactive, decimals, onHover, onAway, onReady }: {
  arena: string;
  tf: Timeframe;
  /** Drag, pinch and hold for details. Off on the small chart, which only opens full screen. */
  interactive: boolean;
  decimals: number;
  onHover?: (c: Ohlc | null) => void;
  onAway?: (away: boolean) => void;
  onReady?: (h: ChartHandle) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<"Candlestick"> | ISeriesApi<"Line"> | null>(null);
  const last = useRef<CandlestickData<UTCTimestamp> | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { prices } = useGame();
  const ticks: Tick[] | undefined = prices[arena];
  const step = tf === "line" ? 15 : tf;

  // Build the chart and load its candles; again whenever the arena or time frame changes.
  useEffect(() => {
    let gone = false;
    setLoaded(false);
    void (async () => {
      const lw = await import("lightweight-charts");
      const r = await api<{ candles: Row[] }>(`/v1/candles/${arena}?tf=${step}`).catch(() => ({ candles: [] as Row[] }));
      if (gone || !box.current) return;
      const c = lw.createChart(box.current, {
        autoSize: true,
        layout: { background: { type: lw.ColorType.Solid, color: "transparent" }, textColor: "#8B949E", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, attributionLogo: true },
        grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.06)" } },
        rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.12, bottom: 0.08 } },
        timeScale: { borderVisible: false, timeVisible: true, secondsVisible: step === 15, rightOffset: 3, tickMarkFormatter: (t: number) => clock(t, false) },
        localization: { timeFormatter: (t: number) => clock(t, step === 15), priceFormatter: (p: number) => p.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) },
        crosshair: {
          mode: interactive ? lw.CrosshairMode.Normal : lw.CrosshairMode.Hidden,
          vertLine: { color: "rgba(255,255,255,0.55)", labelBackgroundColor: "#FFFFFF" },
          horzLine: { color: "rgba(255,255,255,0.55)", labelBackgroundColor: "#FFFFFF" },
        },
        handleScroll: interactive,
        handleScale: interactive,
      });
      const rows = r.candles;
      if (tf === "line") {
        const s = c.addLineSeries({ color: "#CCFF00", lineWidth: 2, priceLineColor: "#CCFF00", priceLineStyle: lw.LineStyle.Dashed, lastValueVisible: true });
        s.setData(rows.map(([t, , , , cl]) => ({ time: t as UTCTimestamp, value: cl })));
        series.current = s;
      } else {
        const s = c.addCandlestickSeries({
          upColor: "#CCFF00", downColor: "#FF3366", wickUpColor: "#CCFF00", wickDownColor: "#FF3366", borderVisible: false,
          priceLineColor: "#CCFF00", priceLineStyle: lw.LineStyle.Dashed,
        });
        s.setData(rows.map(([t, o, h, l, cl]) => ({ time: t as UTCTimestamp, open: o, high: h, low: l, close: cl })));
        series.current = s;
      }
      const tail = rows[rows.length - 1];
      last.current = tail ? { time: tail[0] as UTCTimestamp, open: tail[1], high: tail[2], low: tail[3], close: tail[4] } : null;
      if (onHover) {
        c.subscribeCrosshairMove((p) => {
          const d = p.time !== undefined && series.current ? p.seriesData.get(series.current) : undefined;
          if (!d) return onHover(null);
          const b = d as Partial<CandlestickData & LineData>;
          if (b.open !== undefined) onHover({ o: b.open, h: b.high!, l: b.low!, c: b.close!, t: p.time as number });
          else if (b.value !== undefined) onHover({ o: b.value, h: b.value, l: b.value, c: b.value, t: p.time as number });
        });
      }
      if (onAway) c.timeScale().subscribeVisibleLogicalRangeChange(() => onAway(c.timeScale().scrollPosition() < -1));
      onReady?.({ now: () => c.timeScale().scrollToRealTime() });
      chart.current = c;
      setLoaded(true);
    })();
    return () => {
      gone = true;
      chart.current?.remove();
      chart.current = null;
      series.current = null;
    };
  }, [arena, tf]);

  // Each new price grows the forming candle, or opens the next one.
  const tick = ticks?.[ticks.length - 1];
  useEffect(() => {
    const s = series.current;
    if (!loaded || !s || !tick) return;
    const t = (Math.floor(tick.t / 1000 / step) * step) as UTCTimestamp;
    const prev = last.current;
    if (prev && t < prev.time) return;
    const bar: CandlestickData<UTCTimestamp> =
      prev && prev.time === t
        ? { time: t, open: prev.open, high: Math.max(prev.high, tick.p), low: Math.min(prev.low, tick.p), close: tick.p }
        : { time: t, open: tick.p, high: tick.p, low: tick.p, close: tick.p };
    last.current = bar;
    if (tf === "line") (s as ISeriesApi<"Line">).update({ time: t, value: tick.p } as LineData<UTCTimestamp>);
    else (s as ISeriesApi<"Candlestick">).update(bar);
  }, [tick, loaded]);

  return <div ref={box} style={{ position: "absolute", inset: "0" }} />;
}

/** The numbers under the chart: the last hour's high, low and change, and how far the price usually moves in one battle. */
export function useArenaStats(arena: string, battleSec: number): { high: number; low: number; first: number; move: number } | null {
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => {
    let gone = false;
    const load = () => api<{ candles: Row[] }>(`/v1/candles/${arena}?tf=15`).then((r) => !gone && setRows(r.candles)).catch(() => {});
    void load();
    const t = setInterval(load, 60_000);
    return () => {
      gone = true;
      clearInterval(t);
    };
  }, [arena]);
  if (!rows?.length) return null;
  const hour = rows.slice(-240);
  // The typical move: the middle of every battle-length change across the last two hours, as a fraction.
  const k = Math.max(1, Math.round(battleSec / 15));
  const moves: number[] = [];
  for (let i = k; i < rows.length; i++) moves.push(Math.abs(rows[i]![4] - rows[i - k]![4]) / rows[i - k]![4]);
  moves.sort((a, b) => a - b);
  return {
    high: Math.max(...hour.map((r) => r[2])),
    low: Math.min(...hour.map((r) => r[3])),
    first: hour[0]![1],
    move: moves.length ? moves[Math.floor(moves.length / 2)]! : 0,
  };
}
